import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown, Check, X } from "lucide-react";
import { twMerge } from "tailwind-merge";

const SELECT_ALL_LABEL = "Select all";

type MultiSelectProps = {
  readonly label: string;
  readonly options: readonly string[];
  readonly value: string[];
  readonly onChange: (selected: string[]) => void;
  readonly error?: string;
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
};

export const MultiSelect = ({
  label,
  options,
  value,
  onChange,
  error,
  required = false,
  placeholder = "Select options",
  className
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerId = `multiselect-${label.toLowerCase().replaceAll(/\s+/g, "-")}`;

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleToggle = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const handleRemoveChip = (option: string) => {
    onChange(value.filter((v) => v !== option));
  };

  const handleSelectAll = () => {
    onChange([...options]);
  };

  const allSelected = options.length > 0 && value.length === options.length;

  const displayText =
    value.length > 0 ? `${value.length} selected` : placeholder;

  return (
    <div ref={containerRef} className={twMerge("relative space-y-1.5", className)}>
      <label htmlFor={triggerId} className="form-label">
        {label}
        {required && <span className="form-label-asterisk">*</span>}
      </label>

      <button
        id={triggerId}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={twMerge(
          "flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ease-in-out focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1",
          error ? "border-red-400" : "border-gray-300",
          value.length === 0 ? "text-gray-400" : "text-gray-900"
        )}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown
          className={twMerge(
            "ml-2 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ease-in-out",
            isOpen && "rotate-180"
          )}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="listbox"
          aria-multiselectable
        >
          {options.length > 0 && (
            <button
              type="button"
              role="option"
              aria-selected={allSelected}
              onClick={() => {
                handleSelectAll();
              }}
              className="flex w-full items-center gap-2.5 border-b border-gray-100 px-3 py-2 text-left text-sm font-medium text-primary-600 transition-colors hover:bg-gray-50"
            >
              {SELECT_ALL_LABEL}
            </button>
          )}
          {options.map((option) => {
            const checked = value.includes(option);

            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => {
                  handleToggle(option);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                <span
                  className={twMerge(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                    checked
                      ? "border-primary-600 bg-primary-600 text-white"
                      : "border-gray-300 bg-white"
                  )}
                >
                  {checked && <Check className="h-3 w-3" strokeWidth={3} aria-hidden />}
                </span>
                {option}
              </button>
            );
          })}
        </div>
      )}

      {/* Removable chips for selected items */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700"
            >
              {item}
              <button
                type="button"
                onClick={() => handleRemoveChip(item)}
                className="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-primary-500 transition-all duration-200 ease-in-out hover:bg-primary-100 hover:text-primary-700"
                aria-label={`Remove ${item}`}
              >
                <X className="h-2.5 w-2.5" strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
